import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TRADES } from "@/lib/trades";
import { Users, Plus, Pencil, Trash2, Loader2, Contact, Lock } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", trade: "", notes: "" };

export function VendorsCard() {
  const { user } = useAuth();
  const isSandbox = user?.subscription?.status === "sandbox";
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/vendors"); setVendors(data || []); }
    catch { /* table may not exist yet */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (v) => {
    setForm({ name: v.name || "", email: v.email || "", phone: v.phone || "", trade: v.trade || "", notes: v.notes || "" });
    setEditId(v.id); setOpen(true);
  };

  const canImport = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const importContact = async () => {
    try {
      const picked = await navigator.contacts.select(["name", "email", "tel"], { multiple: false });
      const c = picked && picked[0];
      if (c) setForm((f) => ({
        ...f,
        name: (c.name && c.name[0]) || f.name,
        email: (c.email && c.email[0]) || f.email,
        phone: (c.tel && c.tel[0]) || f.phone,
      }));
    } catch { /* cancelled */ }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editId) await api.patch(`/vendors/${editId}`, form);
      else await api.post("/vendors", form);
      setOpen(false); await load(); toast.success("Vendor saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    setVendors((a) => a.filter((v) => v.id !== id));
    try { await api.delete(`/vendors/${id}`); } catch { toast.error("Delete failed"); load(); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="vendors-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4 text-emerald-500" /> Vendors</div>
        {isSandbox ? (
          <Button size="sm" variant="outline" onClick={() => window.dispatchEvent(new Event("sp:convert"))} data-testid="vendor-add-locked" className="h-8 gap-1"><Lock className="h-3.5 w-3.5" /> Unlock</Button>
        ) : (
          <Button size="sm" onClick={openNew} data-testid="vendor-add-btn" className="h-8 gap-1 bg-emerald-500 hover:bg-emerald-600"><Plus className="h-4 w-4" /> Add</Button>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Your trade rolodex — used to auto-match and send punch lists.</p>
      {isSandbox && (
        <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground" data-testid="vendors-locked-note">
          <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> Save your vendors to auto-match and dispatch punch lists — free when you start your 15-day trial.
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
      ) : vendors.length === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground" data-testid="vendors-empty">No vendors yet.</div>
      ) : (
        <div className="mt-3 space-y-2">
          {vendors.map((v) => (
            <div key={v.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`vendor-${v.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold">{v.name}</span>
                  {v.trade && <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400">{v.trade}</span>}
                </div>
                <div className="truncate text-xs text-muted-foreground">{[v.email, v.phone].filter(Boolean).join(" · ") || "No contact info"}</div>
              </div>
              <button onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(v.id)} data-testid={`vendor-delete-${v.id}`} className="text-muted-foreground hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="vendor-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
          {canImport && (
            <Button variant="outline" onClick={importContact} data-testid="vendor-import-btn" className="gap-1"><Contact className="h-4 w-4" /> Import from contacts</Button>
          )}
          <div className="space-y-3">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vendor name *" data-testid="vendor-name" className="h-11" />
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="vendor-email" className="h-11" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="vendor-phone" className="h-11" />
            <select
              value={form.trade}
              onChange={(e) => setForm({ ...form, trade: e.target.value })}
              data-testid="vendor-trade"
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Trade…</option>
              {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="vendor-save" className="bg-emerald-500 hover:bg-emerald-600">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
